import { FeatureFlags } from 'libs/end-to-end/src/feature-flag';
import { MyUser } from 'libs/end-to-end/src/my-user';
import { ObservationLocationType } from 'libs/end-to-end/src/observation-upload-type';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { apiLogout } from 'apps/sigma2-ui/src/lib/api';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { generateLogoutLink as apiGenerateLogoutLink } from 'apps/sigma2-ui/src/lib/api';
import { loginWithEmailAndPassword } from 'apps/sigma2-ui/src/lib/api';
import { loginWithSSOCode } from 'apps/sigma2-ui/src/lib/api';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useAlive } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { useUserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-authenticated-api-call';
import { UserCallbacks } from 'apps/sigma2-ui/src/state/user-types';
import { UserContext } from 'apps/sigma2-ui/src/state/user-types';
import { UserHook } from 'apps/sigma2-ui/src/state/user-types';
import { UserHookOptions } from 'apps/sigma2-ui/src/state/user-types';
import { UserRendererProps } from 'apps/sigma2-ui/src/state/user-types';
import { OnLoginErrorFn } from 'apps/sigma2-ui/src/state/user-types';
import { areSearchParamsTheSame } from 'apps/sigma2-ui/src/state/url-utils';

// Extend the Window interface to include the _hsq property
declare global {
  interface Window {
    _hsq?: any[];
  }
}

export const useUser = (options?: UserHookOptions): UserHook => {
  const context = useContext(UserContext);

  useEffect(() => {
    if (options?.onError) {
      context.callbacks.addOnLoginError(options.onError);
    }

    return () => {
      if (options?.onError) {
        context.callbacks.removeOnLoginError(options.onError);
      }
    };
  }, [context, options]);

  const {
    myUserStatus,
    logoutLink,
    myUser,
    isAuthenticated,
    featureFlags,
    login,
    loginWithSSO,
    logout,
    startLoggingOut,
    userAuthenticatedApiCall,
    loadUser,
    loadFeatureFlags,
  } = context;

  return useMemo(() => {
    return {
      myUserStatus,
      logoutLink,
      myUser,
      isAuthenticated,
      featureFlags,
      login,
      loginWithSSO,
      logout,
      startLoggingOut,
      userAuthenticatedApiCall,
      loadUser,
      loadFeatureFlags,
    };
  }, [
    myUserStatus,
    logoutLink,
    myUser,
    isAuthenticated,
    featureFlags,
    login,
    loginWithSSO,
    logout,
    startLoggingOut,
    userAuthenticatedApiCall,
    loadUser,
    loadFeatureFlags,
  ]);
};

interface LoggingInUser {
  status: 'logging_in';
  previousMyUser: MyUser | null;
}

interface LoggingOutUser {
  status: 'logging_out';
  logoutLink: string | null;
}

interface LoggedOutUser {
  status: 'logged_out';
}

interface LoggedInUser {
  status: 'logged_in';
  myUser: MyUser;
}

type MyUserState =
  | LoggingInUser
  | LoggedOutUser
  | LoggedInUser
  | LoggingOutUser;

export const UserRenderer = ({ children }: UserRendererProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isLogoutCallbackPage = location.pathname === '/callback-logout';

  const databaseIdSearchParam = useMemo(() => {
    const paramValue = searchParams.get('databaseId');

    if (!paramValue) {
      return null;
    }

    return paramValue;
  }, [searchParams]);

  const databaseRevisionIdSearchParam = useMemo(() => {
    const paramValue = searchParams.get('databaseRevisionId');

    if (!paramValue) {
      return null;
    }

    return paramValue;
  }, [searchParams]);

  const organizationIdSearchParam = useMemo(() => {
    const paramValue = searchParams.get('organizationId');

    if (!paramValue) {
      return null;
    }

    return paramValue;
  }, [searchParams]);

  const setOrgIdAndDbIdSearchParams = useCallback(
    ({
      newDatabaseIdValue,
      newOrganizationIdValue,
      newDatabaseRevisionIdValue,
    }: {
      newDatabaseIdValue: string | null;
      newOrganizationIdValue: string | null;
      newDatabaseRevisionIdValue: string | null;
    }) => {
      const newParams = new URLSearchParams(searchParams);

      if (newDatabaseIdValue === null && newParams.has('databaseId')) {
        newParams.delete('databaseId');
      }

      if (
        newDatabaseIdValue !== null &&
        newParams.get('databaseId') !== newDatabaseIdValue
      ) {
        newParams.set('databaseId', newDatabaseIdValue);
      }

      if (
        newDatabaseRevisionIdValue === null &&
        newParams.has('databaseRevisionId')
      ) {
        newParams.delete('databaseRevisionId');
      }

      if (
        newDatabaseRevisionIdValue !== null &&
        newParams.get('databaseRevisionId') !== newDatabaseRevisionIdValue
      ) {
        newParams.set('databaseRevisionId', newDatabaseRevisionIdValue);
      }

      if (newOrganizationIdValue === null && newParams.has('organizationId')) {
        newParams.delete('organizationId');
      }

      if (
        newOrganizationIdValue !== null &&
        newParams.get('organizationId') !== newOrganizationIdValue
      ) {
        newParams.set('organizationId', newOrganizationIdValue);
      }

      setSearchParams((existing) => {
        return areSearchParamsTheSame(existing, newParams)
          ? existing
          : newParams;
      });
    },
    [searchParams, setSearchParams],
  );

  const analytics = useAnalytics();

  const isAlive = useAlive();
  const [myUserState, setMyUserState] = useState<MyUserState | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags | null>(null);

  const callbacks = useRef(new UserCallbacks());

  const userAuthenticatedApiCall = useUserAuthenticatedApiCall();

  useEffect(() => {
    uiAppLogger.debug(JSON.stringify(myUserState, null, 2));
  }, [myUserState]);

  const loadFeatureFlags = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive()) {
        return;
      }

      const featureFlagsResult = await userAuthenticatedApiCall({
        responseSchema: FeatureFlags,
        url: `${getApiBaseUrl()}/feature-flags`,
        requestOptions: {
          method: 'GET',
        },
      });

      if (!isAlive()) {
        return;
      }

      if (
        featureFlagsResult === null ||
        featureFlagsResult.resultType === 'err'
      ) {
        setFeatureFlags(null);

        return;
      }

      setFeatureFlags(featureFlagsResult.value);
    },
    [userAuthenticatedApiCall],
  );

  const loadUser = useAsyncCallback(
    // eslint-disable-next-line complexity
    async (
      isAlive: IsAliveFn,
      {
        observation,
        organizationId,
        onLoaded,
      }: {
        observation: ObservationLocationType | null;
        organizationId: string | null;
        onLoaded?: (newObservation: ObservationLocationType | null) => void;
      },
    ) => {
      uiAppLogger.info('observation', observation);
      if (!isAlive()) {
        return;
      }

      hasLoadedUserOnce.current = true;

      setMyUserState((prev) => ({
        previousMyUser:
          prev && prev.status === 'logged_in' ? prev.myUser : null,
        status: 'logging_in',
      }));

      let url = getApiBaseUrl() + '/me';

      if (observation || organizationId) {
        const p = new URLSearchParams();
        if (observation?.databaseId) {
          p.set('databaseId', observation.databaseId);
        }

        if (observation?.databaseRevisionId) {
          p.set('databaseRevisionId', observation.databaseRevisionId);
        }

        if (organizationId) {
          p.set('organizationId', organizationId);
        }
        url += `?${p.toString()}`;
      }

      setOrgIdAndDbIdSearchParams({
        newDatabaseIdValue: observation?.databaseId ?? null,
        newOrganizationIdValue: organizationId,
        newDatabaseRevisionIdValue: observation?.databaseRevisionId ?? null,
      });

      // Load their user.
      const userResult = await userAuthenticatedApiCall({
        responseSchema: MyUser,
        url,
        requestOptions: {
          method: 'GET',
        },
      });

      if (!userResult || !isAlive()) {
        return;
      }

      let newObservation: ObservationLocationType | null = null;
      let newOrganizationId: string | null = null;

      if (userResult.resultType === 'err') {
        setMyUserState({
          status: 'logged_out',
        });
      } else {
        analytics.login(userResult.value);

        if (userResult.value.organization) {
          analytics.registerOrganization(userResult.value.organization);
        }

        setMyUserState({
          status: 'logged_in',
          myUser: userResult.value,
        });
        setIsAuthenticated(true);

        loadFeatureFlags();

        newObservation =
          userResult.value.organization?.selectedObservationLocation ?? null;

        newOrganizationId = userResult.value.organization?.id ?? null;
      }

      const newOrgId = newOrganizationId
        ? newOrganizationId
        : organizationIdSearchParam;

      setOrgIdAndDbIdSearchParams({
        newDatabaseIdValue: newObservation?.databaseId ?? databaseIdSearchParam,
        newOrganizationIdValue: newOrgId,
        newDatabaseRevisionIdValue: newObservation?.databaseRevisionId ?? null,
      });

      if (onLoaded) {
        onLoaded(newObservation);
      }
    },
    [
      userAuthenticatedApiCall,
      setOrgIdAndDbIdSearchParams,
      databaseIdSearchParam,
      organizationIdSearchParam,
      analytics,
      loadFeatureFlags,
    ],
  );

  // Without this, we would end up loading their user every time the search params change.
  const hasLoadedUserOnce = useRef(false);

  // Load user if needed.
  useAsyncEffect(
    // Async effects are safe when using `useAsyncEffect` along with `isAlive`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive) => {
      if (
        !isAlive() ||
        hasLoadedUserOnce.current ||
        isLogoutCallbackPage ||
        myUserState?.status === 'logging_out'
      ) {
        return;
      }

      hasLoadedUserOnce.current = true;

      loadUser({
        observation: databaseIdSearchParam
          ? {
              type: 'db-revision',
              databaseId: databaseIdSearchParam,
              // The me.controller.ts file supports the empty revision ID as a way of saying "latest" without having to change a ton of other code.
              databaseRevisionId: databaseRevisionIdSearchParam ?? '',
            }
          : null,
        organizationId: organizationIdSearchParam ?? null,
      });
    },
    [
      loadUser,
      databaseIdSearchParam,
      organizationIdSearchParam,
      databaseRevisionIdSearchParam,
      isLogoutCallbackPage,
      myUserState?.status,
    ],
  );

  const login = useAsyncCallback(
    async (isAlive: IsAliveFn, email: string, password: string) => {
      if (!isAlive()) {
        return;
      }

      const loginResult = await loginWithEmailAndPassword(email, password);

      if (!isAlive()) {
        return;
      }

      if (loginResult.resultType === 'err') {
        setMyUserState({ status: 'logged_out' });
        for (const onLoginError of callbacks.current.onLoginError) {
          onLoginError(loginResult.error);
        }

        return;
      }

      hasLoadedUserOnce.current = true;
      loadUser({
        observation: databaseIdSearchParam
          ? {
              type: 'db-revision',
              databaseId: databaseIdSearchParam,
              databaseRevisionId: databaseRevisionIdSearchParam ?? '',
            }
          : null,
        organizationId: organizationIdSearchParam ?? null,
      });
    },
    [
      callbacks,
      loadUser,
      databaseIdSearchParam,
      databaseRevisionIdSearchParam,
      organizationIdSearchParam,
    ],
  );

  const loginWithSSO = useAsyncCallback(
    async (isAlive: IsAliveFn, code: string, redirectUri: string) => {
      if (!isAlive()) {
        return;
      }

      const loginResult = await loginWithSSOCode(code, redirectUri);

      if (!isAlive()) {
        return;
      }

      if (loginResult.resultType === 'err') {
        setMyUserState({ status: 'logged_out' });
        for (const onLoginError of callbacks.current.onLoginError) {
          onLoginError(loginResult.error);
        }

        return;
      }

      hasLoadedUserOnce.current = true;
      loadUser({
        observation: databaseIdSearchParam
          ? {
              type: 'db-revision',
              databaseId: databaseIdSearchParam,
              databaseRevisionId: databaseRevisionIdSearchParam ?? '',
            }
          : null,
        organizationId: organizationIdSearchParam ?? null,
      });
    },
    [
      loadUser,
      databaseIdSearchParam,
      databaseRevisionIdSearchParam,
      organizationIdSearchParam,
    ],
  );

  const logout = useAsyncCallback(async (isAlive) => {
    if (!isAlive()) {
      return;
    }

    await apiLogout();

    if (!isAlive()) {
      return;
    }

    setMyUserState({ status: 'logged_out' });
    setIsAuthenticated(false);
  }, []);

  const startLoggingOut = useAsyncCallback(
    async (isAlive, logoutUri: string) => {
      if (!isAlive()) {
        return;
      }

      const logoutLinkResult = await apiGenerateLogoutLink(logoutUri);
      if (logoutLinkResult.resultType === 'err') {
        return;
      }

      setMyUserState({
        status: 'logging_out',
        logoutLink: logoutLinkResult.value,
      });
    },
    [],
  );

  const value = useMemo(
    () => ({
      myUserStatus: !myUserState ? null : myUserState.status,
      logoutLink:
        myUserState?.status === 'logging_out' ? myUserState.logoutLink : null,
      myUser:
        myUserState?.status === 'logged_in'
          ? myUserState.myUser
          : myUserState?.status === 'logging_in'
            ? myUserState.previousMyUser
            : null,
      isAuthenticated,
      featureFlags,
      login,
      loginWithSSO,
      logout,
      startLoggingOut,
      userAuthenticatedApiCall,
      loadUser,
      loadFeatureFlags,
      callbacks: callbacks.current,
    }),
    [
      myUserState,
      isAuthenticated,
      featureFlags,
      login,
      loginWithSSO,
      logout,
      startLoggingOut,
      userAuthenticatedApiCall,
      loadUser,
      loadFeatureFlags,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
